/**
 * Glosa, pelo recorte que a decisão pede.
 *
 * Não são cinco telas: é a mesma pergunta — **por que deixaram de pagar** —
 * com o agrupamento mudando conforme o que se vai fazer com a resposta.
 *
 *   causa        → o que corrigir na origem, antes do envio
 *   competência  → se o problema está piorando ou já passou
 *   arquivo      → qual remessa concentrou o problema
 *   protocolo    → quanto daquele processo não entrou, e por quê
 *   procedimento → qual exame é sistematicamente recusado
 *
 * Agrupar por causa foi o primeiro recorte porque foi o que revelou o
 * essencial na amostra da Unimed: **44 das 56 glosas eram a mesma causa** —
 * "procedimento contido no principal". Isso não é recusa da operadora, é
 * erro de faturamento, corrigível antes do envio. Uma lista por guia nunca
 * mostraria isso.
 *
 * O total **não muda ao trocar de recorte**. Mudar o agrupamento e ver o
 * número mudar junto faria duvidar do número; ele só muda com o filtro, que
 * é o que a pessoa pediu explicitamente.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import { ponteDaGlosa, visaoGlosas } from '../api/fluxfin';
import {
  Ajuda,
  Alerta,
  Badge,
  Botao,
  Busca,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import { brl, mascararCompetencia, numero } from '../utils/formato';

const RECORTES = [
  { chave: 'causa', rotulo: 'Por causa', ajuda: 'O que corrigir na origem, antes do envio.' },
  { chave: 'competencia', rotulo: 'Por competência', ajuda: 'Se o problema está piorando ou já passou.' },
  { chave: 'arquivo', rotulo: 'Por arquivo', ajuda: 'Qual remessa concentrou o problema.' },
  { chave: 'protocolo', rotulo: 'Por protocolo', ajuda: 'Quanto daquele processo não entrou.' },
  { chave: 'procedimento', rotulo: 'Por procedimento', ajuda: 'Qual exame é recusado com frequência.' },
];

/* O rótulo de cada linha depende do recorte — e só disso. */
function rotuloDaLinha(por, l) {
  if (por === 'causa') {
    return (
      <>
        <span className="ff-mono">{l.codigo_causa || '—'}</span>
        <span style={{ display: 'block', fontSize: 13, color: 'var(--ff-muted)' }}>
          {l.motivo || 'sem motivo informado'}
        </span>
      </>
    );
  }
  if (por === 'competencia') return <span className="ff-mono">{l.competencia || '—'}</span>;
  if (por === 'arquivo') {
    return (
      <>
        <span className="ff-mono">{l.arquivo}</span>
        <span style={{ display: 'block', fontSize: 13, color: 'var(--ff-muted)' }}>
          competência {l.competencia || '—'}
        </span>
      </>
    );
  }
  if (por === 'protocolo') {
    return l.sem_protocolo ? (
      <>
        <span style={{ color: 'var(--ff-muted)' }}>sem protocolo</span>
        <span style={{ display: 'block', fontSize: 13, color: 'var(--ff-muted)' }}>
          a guia ainda não foi conciliada — é glosa sem processo a que atribuir
        </span>
      </>
    ) : (
      <span className="ff-mono">{l.protocolo}</span>
    );
  }
  return (
    <>
      <span className="ff-mono">{l.codigo_servico || '—'}</span>
      <span style={{ display: 'block', fontSize: 13, color: 'var(--ff-muted)' }}>
        {l.descricao_servico || 'nome não disponível neste documento'}
      </span>
    </>
  );
}

export default function Glosas() {
  const navigate = useNavigate();
  const [por, setPor] = useState('causa');
  const [competencia, setCompetencia] = useState('');
  const [aplicada, setAplicada] = useState('');
  const [dados, setDados] = useState(null);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');
  const [ponte, setPonte] = useState(null);

  const carregar = useCallback(() => {
    setDados(null);
    visaoGlosas({ por, competencia: aplicada || undefined })
      .then((r) => setDados(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível carregar as glosas.')));
  }, [por, aplicada]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // A ponte não depende do recorte nem do filtro: ela descreve o acervo
  // inteiro. Carregada uma vez, fora do `carregar`.
  useEffect(() => {
    ponteDaGlosa()
      .then((r) => setPonte(r.data))
      .catch(() => setPonte(null));
  }, []);

  // Em `useMemo` porque `|| []` cria um array novo a cada render, e isso
  // refaria o filtro abaixo sem nada ter mudado.
  const linhas = useMemo(() => dados?.linhas || [], [dados]);
  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return linhas;
    return linhas.filter((l) =>
      [
        l.motivo,
        l.codigo_causa,
        l.codigo_tiss,
        l.competencia,
        l.arquivo,
        l.protocolo,
        l.codigo_servico,
        l.descricao_servico,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(t))
    );
  }, [linhas, busca]);

  const ord = useOrdenacao(filtrados, 'total', false);
  // O recorte por procedimento já passa de 170 linhas com dois arquivos, e
  // cresce com o histórico — a tabela não pode ser a altura da base.
  const pag = usePaginacao(ord.ordenados, 25);

  // A concentração só é notícia quando é alta, e só faz sentido por causa:
  // 36 itens do mesmo exame não dizem nada sobre o que corrigir.
  const somaQtd = linhas.reduce((s, l) => s + l.qtd, 0);
  const maior = linhas.length ? Math.max(...linhas.map((l) => l.qtd)) : 0;
  const concentracao = somaQtd ? Math.round((maior / somaQtd) * 100) : 0;

  /* O detalhe deixou de ser modal e virou a tela de Auditoria de glosa.
     Com pago e glosado lado a lado sao nove colunas, que nao cabem num
     dialogo sem rolagem horizontal — e o auditor passa tempo ali, entao
     precisa de filtro, paginacao e uma URL que de para salvar e repassar. */
  const abrirAuditoria = (l) => {
    const q = new URLSearchParams();
    if (aplicada) q.set('competencia', aplicada);
    if (por === 'causa' && l.codigo_causa) q.set('causa', l.codigo_causa);
    if (por === 'competencia' && l.competencia) q.set('competencia', l.competencia);
    if (por === 'arquivo' && l.id_arquivo) q.set('id_arquivo', l.id_arquivo);
    if (por === 'protocolo' && l.protocolo) q.set('protocolo', l.protocolo);
    if (por === 'procedimento' && l.codigo_servico) {
      q.set('codigo_servico', l.codigo_servico);
    }
    navigate(`/glosas/auditoria?${q.toString()}`);
  };

  if (erro && !dados) {
    return (
      <>
        <PageHeader titulo="Glosas" />
        <Alerta tipo="erro">{erro}</Alerta>
      </>
    );
  }

  return (
    <>
      <PageHeader
        titulo="Glosas"
        subtitulo="Por que a operadora deixou de pagar — pelo recorte que a decisão pede."
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>

      {/* O recorte vem antes de tudo: é ele que define o que a tabela
          significa. */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {RECORTES.map((r) => (
          <button
            key={r.chave}
            type="button"
            className={`ff-btn ${por === r.chave ? 'ff-btn--primario' : 'ff-btn--secundario'}`}
            onClick={() => setPor(r.chave)}
            title={r.ajuda}
          >
            {r.rotulo}
          </button>
        ))}
      </div>

      {por === 'causa' && concentracao >= 50 && (
        <Alerta tipo="info">
          Uma única causa responde por <strong>{concentracao}% das glosas</strong>. Quando a
          concentração é essa, quase nunca é recusa caso a caso da operadora — é algo
          sistemático no envio, que dá para corrigir na origem em vez de recorrer guia a
          guia todo mês.
        </Alerta>
      )}

      {ponte && ponte.total_recusado > 0 && <Ponte ponte={ponte} />}

      <Painel
        titulo={RECORTES.find((r) => r.chave === por)?.rotulo}
        subtitulo={
          dados
            ? `${numero(dados.resumo.itens)} itens · ${numero(dados.resumo.guias)} guias · ${brl(
                dados.resumo.total
              )} glosados`
            : undefined
        }
        rodape={filtrados.length > 0 && <Paginacao {...pag} rotulo="linhas" />}
        busca={
          linhas.length > 0 && (
            <Busca valor={busca} aoMudar={setBusca} placeholder="Buscar neste recorte" />
          )
        }
        acoes={
          <div className="d-flex gap-2 align-items-center">
            <input
              className="form-control form-control-sm"
              style={{ width: 130, borderRadius: 8 }}
              placeholder="Competência"
              inputMode="numeric"
              value={competencia}
              onChange={(e) => setCompetencia(mascararCompetencia(e.target.value))}
              onKeyDown={(e) => e.key === 'Enter' && setAplicada(competencia)}
            />
            <Botao variante="secundario" pequeno onClick={() => setAplicada(competencia)}>
              Filtrar
            </Botao>
            {aplicada && (
              <Botao
                variante="secundario"
                pequeno
                onClick={() => {
                  setCompetencia('');
                  setAplicada('');
                }}
              >
                Limpar
              </Botao>
            )}
          </div>
        }
      >
        {!dados ? (
          <LoadingSpinner />
        ) : linhas.length === 0 ? (
          <Vazio
            icone="bi-scissors"
            titulo="Nenhuma glosa"
            descricao={
              aplicada
                ? `Nenhuma glosa na competência ${aplicada}.`
                : 'Nenhum arquivo de glosa foi lançado ainda.'
            }
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                <Th campo="chave" estado={ord}>
                  {RECORTES.find((r) => r.chave === por)?.rotulo.replace('Por ', '')}
                </Th>
                {por === 'causa' && (
                  <Th campo="codigo_tiss" estado={ord}>
                    Cód. TISS
                    <Ajuda titulo="Cód. TISS" origem="Extrato de glosa da operadora">
                      O código padronizado pela ANS. É o único que permite comparar
                      operadoras entre si — o outro código é dialeto de cada uma.
                    </Ajuda>
                  </Th>
                )}
                <Th campo="qtd" estado={ord} alinhar="right">
                  Ocorrências
                </Th>
                <Th campo="guias" estado={ord} alinhar="right">
                  Guias
                </Th>
                <Th campo="competencias" estado={ord} alinhar="right">
                  Competências
                </Th>
                <Th campo="total" estado={ord} alinhar="right">
                  Valor glosado
                </Th>
                <Th alinhar="right">Detalhe</Th>
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((l) => (
                <tr key={String(l.chave)}>
                  <td className="col-principal">{rotuloDaLinha(por, l)}</td>
                  {por === 'causa' && <td className="col-codigo">{l.codigo_tiss || '—'}</td>}
                  <td className="col-qtd">{numero(l.qtd)}</td>
                  <td className="col-qtd">{numero(l.guias)}</td>
                  <td className="col-qtd">
                    {numero(l.competencias)}
                    {l.recorrente && (
                      <Badge tom="atencao" style={{ marginLeft: 6 }}>
                        recorrente
                      </Badge>
                    )}
                  </td>
                  <td className="col-valor">{brl(l.total)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Botao
                      variante="secundario"
                      pequeno
                      onClick={() => abrirAuditoria(l)}
                      title="Abre a auditoria com este recorte: pago e glosado lado a lado, item a item."
                    >
                      Auditar
                    </Botao>
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={por === 'causa' ? 5 : 4}>
                  <strong>Total</strong>
                </td>
                <td className="col-valor">
                  <strong>{brl(dados.resumo.total)}</strong>
                </td>
                <td />
              </tr>
            </tbody>
          </Tabela>
        )}
      </Painel>

    </>
  );
}


/**
 * Por que esta tela e a de Títulos mostram números diferentes.
 *
 * Os dois estão certos e medem coisas distintas: aqui aparece **tudo que a
 * operadora recusou**; o título carrega só o que é dele. Sem a ponte, quem
 * comparasse as duas via uma diferença de milhares de reais sem explicação —
 * e a reação natural seria duvidar dos dois números.
 *
 * Cada degrau é uma situação com conduta própria, e por isso leva ao lugar
 * onde se age sobre ela.
 */
function Ponte({ ponte }) {
  return (
    <Painel
      titulo="Desta tela até o título"
      subtitulo="Nem toda glosa entra no título — e cada motivo tem um lugar para resolver."
    >
      <ol className="ff-ponte">
        <li>
          <span className="rot">Recusado pela operadora</span>
          <span className="ff-mono val">{brl(ponte.total_recusado)}</span>
          <span className="nota">{numero(ponte.qtd_itens)} itens nesta tela</span>
        </li>
        {ponte.degraus
          .filter((d) => Number(d.valor) > 0)
          .map((d) => (
            <li key={d.chave} className="degrau">
              <span className="rot">
                <Link to={d.onde}>{d.rotulo}</Link>
              </span>
              <span className="ff-mono val">− {brl(d.valor)}</span>
              <span className="nota">{d.explicacao}</span>
            </li>
          ))}
        <li className="fim">
          <span className="rot">Entra no título</span>
          <span className="ff-mono val">{brl(ponte.no_titulo)}</span>
          <span className="nota">é o glosado de Saldo a receber e de Títulos</span>
        </li>
      </ol>
    </Painel>
  );
}